/**
 * O3Zta 语言设置页 — frame O3Zta @ ui/dogdisk-user.pen 页面-语言设置
 *
 * 入口:uOqAt 设置 hub menuItem3 (zBaY4) → /profile/language
 *
 * 5 lang radio per frontend-rules §10:zh-CN / zh-TW / en-US / ja-JP / ko-KR;
 * 选中触发:
 *  - useMutation PATCH profiles.locale(走 supabase PostgREST RLS)
 *  - i18n.changeLanguage(lang)
 *  - localStorage 持久化(dogdisk_locale)
 *  - invalidate profile query
 *
 * 注:当前 i18n init resources 含 zh-CN / en / ja / ko / es 5 个 bundle;
 *    zh-TW / en-US / ja-JP / ko-KR 选中后 i18next 会 fallback 到对应短码 base 或 zh-CN。
 *    实际翻译 bundle 补齐由 Phase backlog 跟进(不阻塞本 frame)。
 *
 * Spec:docs/superpowers/specs/2026-05-26-batch-d-user-routes-design.md §4.1
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Spinner, getSupabase, useCurrentUser, cn } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'
import { i18n } from '../../i18n'

const F = 'O3Zta'

type LangCode = 'zh-CN' | 'zh-TW' | 'en-US' | 'ja-JP' | 'ko-KR'

const LANGS: ReadonlyArray<{ code: LangCode; label: string }> = [
  { code: 'zh-CN', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'en-US', label: 'English' },
  { code: 'ja-JP', label: '日本語' },
  { code: 'ko-KR', label: '한국어' },
]

const STORAGE_KEY = 'dogdisk_locale'

/** 把 5-lang frontend-rules 码映射到 i18n init 已注册的 bundle key */
function mapToI18nKey(code: LangCode): string {
  switch (code) {
    case 'zh-CN':
      return 'zh-CN'
    case 'zh-TW':
      return 'zh-CN' // fallback;P1 加 zh-TW bundle
    case 'en-US':
      return 'en'
    case 'ja-JP':
      return 'ja'
    case 'ko-KR':
      return 'ko'
  }
}

export function LanguagePage() {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const qc = useQueryClient()
  const [selected, setSelected] = useState<LangCode>('zh-CN')

  const query = useQuery<{ locale: string | null } | null>({
    queryKey: ['user', 'me', 'profile', 'locale', user?.user_id] as const,
    enabled: Boolean(user),
    queryFn: async () => {
      if (!user) return null
      const sb = getSupabase()
      const { data, error } = await sb.from('v_profile_self').select('locale').single()
      if (error) throw error
      return (data as { locale: string | null }) ?? null
    },
  })

  useEffect(() => {
    // 初始 selected:profile.locale > localStorage > 'zh-CN'
    const fromProfile = query.data?.locale
    const fromStorage =
      typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
    const initial = fromProfile || fromStorage || 'zh-CN'
    if (LANGS.some((l) => l.code === initial)) {
      setSelected(initial as LangCode)
    }
  }, [query.data])

  const setLocale = useMutation({
    mutationFn: async (code: LangCode) => {
      if (!user) throw new Error('未登录')
      const sb = getSupabase()
      const { error } = await sb
        .from('profiles')
        .update({ locale: code })
        .eq('user_id', user.user_id)
      if (error) throw error
      return code
    },
    onSuccess: (code) => {
      setSelected(code)
      void i18n.changeLanguage(mapToI18nKey(code))
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, code)
      }
      void qc.invalidateQueries({ queryKey: ['user', 'me', 'profile'] })
    },
  })

  // 等 user 加载完成再渲染 radio,否则 click 时 mutationFn 因 `!user` 抛错,
  // PATCH 永远不会发出(spec waitForRequest /rest/v1/profiles 等不到)。
  if (query.isLoading || !user) {
    return (
      <AppLayout>
        <MobileScreen>
          <TopBar title="语言" fallback="/profile/settings" />
          <div className="flex flex-1 items-center justify-center py-12">
            <Spinner className="text-gray-400" />
          </div>
        </MobileScreen>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <div data-testid={F}>
          <TopBar title="语言" fallback="/profile/settings" />
          {/* back-btn(供 e2e click;TopBar 内置返回箭头已可见,此为 testid 锚点) */}
          <button
            type="button"
            data-testid={`${F}-back-btn`}
            onClick={() => navigate('/profile/settings')}
            className="absolute left-3 top-[44px] h-9 w-9 opacity-0"
            aria-label="返回设置"
          />

          <section className="mt-3 bg-white">
            {LANGS.map((lang) => {
              const isActive = selected === lang.code
              return (
                <button
                  key={lang.code}
                  type="button"
                  role="radio"
                  data-testid={`${F}-lang-${lang.code}`}
                  aria-checked={isActive}
                  disabled={setLocale.isPending}
                  onClick={() => setLocale.mutate(lang.code)}
                  className={cn(
                    'flex w-full items-center justify-between px-4 py-3 text-left text-[15px] border-b border-border-subtle last:border-0',
                    isActive ? 'text-brand-700' : 'text-ink',
                    setLocale.isPending ? 'opacity-60' : 'active:bg-surface-100',
                  )}
                >
                  <span>{lang.label}</span>
                  {isActive && (
                    <svg
                      aria-hidden
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                  )}
                </button>
              )
            })}
          </section>
        </div>
      </MobileScreen>
    </AppLayout>
  )
}

export default LanguagePage
